import skillsService from '../../../API/Services/SkillsService';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";

//  Context
import { useLanguage } from '../../../API/Context/LanguageContext';

//  Widgets
import GenericSearchBar from '../../Widgets/MultiRole/GenericSearchBar/GenericSearchBar.tsx';

//  Config
import { VocabSkills } from '../../../ConfigFiles/multilingual/VocabSkills';
import ColorSpecialties from '../../../ConfigFiles/colors/color_specialties';

// NOTE: Renamed prop from skills_data to initialData to be consistent with fixes
const WidgetSkillsSelector = forwardRef(({ initialData = [], onChange }, ref) => {
    //  Consume context
    const { lan } = useLanguage();

  // Initialize state with data, ensuring each skill has necessary fields for rendering
  // 🎯 FIX 3: Initialize state once using the functional update pattern (removed redundant map)
  const [selectedSkills, setSelectedSkills] = useState(() =>
    (Array.isArray(initialData) ? initialData : []).map(skill => ({
      name: skill.name || skill, 
      // Ensure unique ID for local tracking
      skill_id: skill.id || skill.skill_id || Math.random().toString(36).substring(7), 
      category: skill.category || 1,
      deleted: skill.deleted || false,
    }))
  );
  
  const skillsServiceInstance = new skillsService();

  // Use a ref to store the last successfully submitted data (by value)
  const lastSubmittedDataRef = useRef(null); 


  // 2. Notify parent (RegisterProfessionalPage) of changes
  useEffect(() => {
    // Filter active skills
    const activeSkills = selectedSkills.filter(s => !s.deleted);
    // 🎯 FIX 4: If skills are optional (which they usually are), pass [] if none are selected.
    // If skills were mandatory, you'd pass null here if activeSkills.length === 0.
    const outputData = activeSkills.length > 0 ? activeSkills : []; 

    // FIX: CRITICAL DEEP COMPARISON CHECK
    if (JSON.stringify(outputData) !== JSON.stringify(lastSubmittedDataRef.current)) {
         lastSubmittedDataRef.current = outputData;
        // Pass the array (which can be empty) back to the parent.
        onChange?.(outputData);
    }

  }, [selectedSkills, onChange]); // Depend on selectedSkills and onChange

  const handleAdd = (skill) => {
    // 🎯 FIX 5: Use a generated ID if the API skill doesn't have one (for new/unsaved items)
    const skillIdentifier = skill.id || Math.random().toString(36).substring(7);

    const existingIndex = selectedSkills.findIndex((s) => s.skill_id === skillIdentifier);
    if (existingIndex !== -1) {
      // If it exists and is marked deleted, undelete it
      if (selectedSkills[existingIndex].deleted) {
        setSelectedSkills((prev) =>
          prev.map((s) =>
            // Use the proper identifier for comparison
            s.skill_id === skillIdentifier ? { ...s, deleted: false } : s
          )
        );
      }
    } else {
      // Add a new skill object
      setSelectedSkills((prev) => [
        ...prev,
        {
          name: skill.name,
          skill_id: skillIdentifier, // Use the generated/provided ID
          category: skill.category || 1, // Ensure category is set
          deleted: false,
        },
      ]);
    }
  };
  

  // Handler for removing/marking as deleted a skill
  const handleRemove = (id) => {
    // Mark as deleted (soft delete)
    const updated = selectedSkills.map((s) =>
      s.skill_id === id
        ? { ...s, deleted: true, deleted_on: new Date().toISOString() }
        : s
    );
    setSelectedSkills(updated);
  };

  // Expose a "getData" function to the SlidingWidgetMenu parent via ref
  useImperativeHandle(ref, () => ({
    getData: () => {
      // Return data in the format expected by the API (SlidingWidgetMenu)
      return selectedSkills.map((skill) => ({
        skill_id: skill.skill_id,
        deleted: skill.deleted || false,
      }));
    },
  }));

  return (
    <div className="skills-widget-container">
      <GenericSearchBar
        searchFunction={(query, signal) => skillsServiceInstance.searchSkills(query, signal)}
        renderLabel={(skill) => skill.name || 'Unnamed skill'}
        placeholder={VocabSkills.search_skill[lan]}
        onSelect={(selectedSkill) => handleAdd(selectedSkill)}
      />

      {/* Selected Skills Container */}
      <div className="mb-3">
        <h5>{VocabSkills.current_skills[lan]}</h5>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '8px',
            minHeight: '120px', // Min height instead of fixed
            maxHeight: '200px',
            overflowY: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'flex-start',
          }}
        >
          {selectedSkills
            .filter((skill) => !skill.deleted)
            .map((skill) => (
              <span
                key={skill.skill_id}
                className="badge rounded-pill me-2 mb-2"
                style={{
                  backgroundColor: ColorSpecialties[skill.category] || '#6c757d',
                  color: 'white',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => handleRemove(skill.skill_id)}
              >
                {skill.name}
                <span className="ms-1">×</span>
              </span>
            ))}
            {selectedSkills.filter(s => !s.deleted).length === 0 && (
                <p className="text-muted m-0">No skills selected.</p>
            )}
        </div>
      </div>
    </div>
  );
});

export default WidgetSkillsSelector;